import { useState, useEffect } from "react";
import { Stack, Typography } from "@mui/material";
import BackButton from "@quest_editor/components/Buttons/BackButton";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";
import { useQuests, usePromotions } from "@quest_editor/mocks";
import {
  getPromotionTypeOptions,
  getOwnerTypeOptions,
} from "@quest_editor/utils/view/constLabels";
import { getPromotionLabels } from "@quest_editor/utils/view/promotionLabels";
import ListSection from "./ListSection";
import { ProgressFilters } from "./FilterSection";

const QuestProgressList = () => {
  const { t } = useTranslation("questEditor");
  const [searchParams, setSearchParams] = useSearchParams();

  const getParamValue = (key: string) => searchParams.get(key) || "";

  const [filters, setFilters] = useState<ProgressFilters>({
    promotionIdName: getParamValue("promotionIdName"),
    type: getParamValue("type"),
    company: getParamValue("company"),
    questIdName: getParamValue("questIdName"),
    dueDate: getParamValue("dueDate"),
  });
  const [page, setPage] = useState(Number(getParamValue("page")) || 1);
  const rowsPerPage = 25;

  useEffect(() => {
    setFilters({
      promotionIdName: getParamValue("promotionIdName"),
      type: getParamValue("type"),
      company: getParamValue("company"),
      questIdName: getParamValue("questIdName"),
      dueDate: getParamValue("dueDate"),
    });
    setPage(Number(getParamValue("page")) || 1);
  }, [searchParams]);

  const { data: questsData } = useQuests();
  const quests = questsData?.quests ?? [];

  const { data: promotionsData } = usePromotions();
  const promotions = promotionsData?.promotions ?? [];

  const promotionTypes = getPromotionTypeOptions();
  const ownerTypes = getOwnerTypeOptions();
  const { getTypeLabel } = getPromotionLabels();

  const getPromotionById = (promotionId: string) =>
    promotions.find((p) => p.id === promotionId);

  const rows = quests.map((quest) => {
    const promotion = getPromotionById(quest.promotionId);
    return {
      promotionId: promotion?.id ?? "",
      promotionName: promotion?.name ?? "",
      promotionType: promotion?.type ?? "",
      promotionOwner: promotion?.owner ?? "",
      questId: quest.id,
      questName: quest.name,
      progress: `${quest.progress ?? 0} / ${quest.progressMax}`,
      progressCurrent: quest.progress ?? 0,
      progressTarget: quest.progressMax,
      scheduleStart: quest.startAt ?? "",
      scheduleEnd: quest.endAt ?? "",
    };
  });

  const handleFilterChange = (key: keyof ProgressFilters, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (filters.promotionIdName)
      params.set("promotionIdName", filters.promotionIdName);
    if (filters.type) params.set("type", filters.type);
    if (filters.company) params.set("company", filters.company);
    if (filters.questIdName) params.set("questIdName", filters.questIdName);
    if (filters.dueDate) params.set("dueDate", filters.dueDate);
    params.set("page", "1");
    setSearchParams(params);
    setPage(1);
  };

  const handlePageChange = (newPage: number) => {
    const params = new URLSearchParams(searchParams);
    params.set("page", String(newPage));
    setSearchParams(params);
    setPage(newPage);
  };

  const filteredRows = (() => {
    const promotionIdName = getParamValue("promotionIdName");
    const type = getParamValue("type");
    const company = getParamValue("company");
    const questIdName = getParamValue("questIdName");
    const dueDate = getParamValue("dueDate");

    return rows.filter((row) => {
      const matchesDueDate =
        dueDate === "" ||
        (row.scheduleStart <= dueDate && dueDate <= row.scheduleEnd);

      return (
        (promotionIdName === "" ||
          row.promotionId.includes(promotionIdName) ||
          row.promotionName.includes(promotionIdName)) &&
        (type === "" || row.promotionType === type) &&
        (company === "" || row.promotionOwner === company) &&
        (questIdName === "" ||
          row.questId.includes(questIdName) ||
          row.questName.includes(questIdName)) &&
        matchesDueDate
      );
    });
  })();

  const start = (page - 1) * rowsPerPage;
  const pageRows = filteredRows.slice(start, start + rowsPerPage);

  const pageCount = Math.ceil(filteredRows.length / rowsPerPage);

  return (
    <Stack direction="column" spacing={3} sx={{ flex: 1, minHeight: 0 }}>
      <Typography variant="h5" fontWeight="bold" color="primary">
        {t("quest_progress_list.title")}
      </Typography>

      <ListSection
        filters={filters}
        onFilterChange={handleFilterChange}
        onSearch={handleSearch}
        promotionTypes={promotionTypes}
        ownerTypes={ownerTypes}
        pageRows={pageRows}
        filteredCount={filteredRows.length}
        page={page}
        onPageChange={handlePageChange}
        pageCount={pageCount}
        getTypeLabel={getTypeLabel}
      />

      <BackButton
        component={Link}
        to="/quest_editor"
        sx={{ alignSelf: "flex-start" }}
      >
        {t("quest_progress_list.button.back")}
      </BackButton>
    </Stack>
  );
};

export default QuestProgressList;
